import { Box, Stack, createStyles, keyframes } from '@mantine/core';
import type { EmbeddedPromptOut } from '@repo/api-codegen';
import { animation } from '@repo/theme/primitives';
import { memo } from 'react';
import { RichEditor } from '../../RichEditor';
import { useAIAssistantContext } from '../hooks';
import { AIAssistantMode } from '../types';
import { sanitizePromptMessage } from '../utils';
import { AskQuestionButton } from './AskQuestionButton';
import { MessageToolbar } from './MessageToolbar';
import { PromptAttachments } from './PromptAttachments';

const fadeUp = keyframes({
	'0%': {
		opacity: 0,
		transform: 'translateY(24px)',
	},
	'100%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const fadeUpAnimation = animation(
	fadeUp,
	'duration-300',
	'ease',
	{ opacity: 0, transform: 'translateY(24px)' },
	'forwards'
);

const useStyles = createStyles(
	(
		theme,
		{ isSidebar, canAnimate }: { isSidebar: boolean; canAnimate: boolean }
	) => ({
		userMessageContainer: {
			display: 'flex',
			maxWidth: isSidebar ? '100%' : '90%',
			alignSelf: 'flex-end',
			...(canAnimate && fadeUpAnimation),
		},
		userMessageContent: {
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			borderRadius: `${theme.radius.md} ${theme.radius.md} ${theme.radius.sm} ${theme.radius.md}`,
			padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
			width: '100%',
			overflow: 'auto',
		},
		userToolbar: {
			alignSelf: 'flex-end',
		},
	})
);

export const UserMessage = memo(
	({
		message,
		parentPrompt,
	}: {
		message: EmbeddedPromptOut;
		parentPrompt?: EmbeddedPromptOut;
	}) => {
		const canAnimate = ['pending', 'running'].includes(
			message.status ?? 'pending'
		);

		const store = useAIAssistantContext();
		const { classes } = useStyles({
			isSidebar: store.mode === AIAssistantMode.SIDEBAR,
			canAnimate,
		});

		return (
			<MessageToolbar.Wrapper>
				<Stack
					align="flex-end"
					spacing={0}
					className={classes.userMessageContainer}
				>
					<PromptAttachments message={message} />
					<Box className={classes.userMessageContent}>
						<RichEditor
							readOnly
							value={sanitizePromptMessage(message.prompt)}
						/>
					</Box>
					<MessageToolbar className={classes.userToolbar}>
						<AskQuestionButton
							message={message}
							parentPrompt={parentPrompt}
							source="prompt"
							variant="tertiary"
						/>
					</MessageToolbar>
				</Stack>
			</MessageToolbar.Wrapper>
		);
	}
);
